import React from "react";
import { motion } from "framer-motion";
import { Cpu, Plane, Database } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { useApiCollection } from "../hooks/useApiCollection";

const ICON_BY_SLUG = {
  "virtual-crash": <Cpu className="h-5 w-5" />,
  "bosch-cdr": <Database className="h-5 w-5" />,
  "dji-mavic": <Plane className="h-5 w-5" />,
};

export default function Tools() {
  const { t, lang } = useLanguage();
  const { data: tools } = useApiCollection("/tools", []);

  return (
    <section
      id="tools"
      data-testid="tools-section"
      className="relative bg-[#0A0A0A] py-24 sm:py-28 border-b border-neutral-900"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-12 gap-8 mb-12 items-end">
          <div className="col-span-12 md:col-span-7">
            <div className="overline mb-4">{t.tools.overline}</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
              {t.tools.title}
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:text-right">
            <p className="text-neutral-400 text-base max-w-md leading-relaxed md:ml-auto">
              {t.tools.subtitle}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800">
          {tools.map((tool, idx) => (
            <motion.div
              key={tool.id}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.5, delay: idx * 0.1 }}
              data-testid={`tool-card-${tool.slug}`}
              className="bg-[#0A0A0A] p-8 group hover:bg-[#111] transition-colors"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="h-12 w-12 bg-yellow-500/10 border border-yellow-500/30 flex items-center justify-center text-yellow-400">
                  {ICON_BY_SLUG[tool.slug] || <Cpu className="h-5 w-5" />}
                </div>
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-neutral-600">
                  / {String(tool.order).padStart(2, "0")}
                </span>
              </div>
              <div className="font-mono-tech text-[10px] uppercase tracking-[0.2em] text-yellow-400 mb-2">
                {lang === "ro" ? tool.category_ro : tool.category_en}
              </div>
              <h3 className="font-display font-bold text-white text-2xl mb-3 leading-tight">
                {tool.name}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {lang === "ro" ? tool.desc_ro : tool.desc_en}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
